/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { PlanogramChangeDetailsFragment } from '@/graphql'

export const userName = (
  user: { firstName: string; lastName: string } | null,
  unknownLabel: string,
) =>
  user
    ? `${user.firstName} ${user.lastName}`.trim() || unknownLabel
    : unknownLabel

export const shelves = (segments: string[][][]) =>
  segments.flatMap(segment => segment)

export const facingChanges = (change: PlanogramChangeDetailsFragment) => {
  const previous = shelves(change.previousPlanogram.shapedUpcs)
  const current = shelves(change.newPlanogram.shapedUpcs)
  const shelfCount = Math.max(previous.length, current.length)
  let changed = 0
  for (let shelfIndex = 0; shelfIndex < shelfCount; shelfIndex += 1) {
    const columnCount = Math.max(
      previous[shelfIndex]?.length ?? 0,
      current[shelfIndex]?.length ?? 0,
    )
    for (let columnIndex = 0; columnIndex < columnCount; columnIndex += 1) {
      if (
        previous[shelfIndex]?.[columnIndex] !==
        current[shelfIndex]?.[columnIndex]
      ) {
        changed += 1
      }
    }
  }
  return changed
}

export const summaryText = (
  change: PlanogramChangeDetailsFragment,
  translate: (
    key: string,
    fallback: string,
    params?: Record<
      string,
      string | number | boolean | Date | bigint | null | undefined
    >,
  ) => string,
) => {
  const summary = change.diff.summary
  const facingsChanged = facingChanges(change)
  return translate(
    'planogramHistory.summary',
    '{productsRemoved, plural, one {# product} other {# products}} removed, {productsAdded, plural, one {# product} other {# products}} added, {facingsChanged, plural, one {# facing} other {# facings}} changed',
    {
      productsRemoved: summary.productsRemoved,
      productsAdded: summary.productsAdded,
      facingsChanged,
    },
  )
}
